import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Alert,
    Button,
    Card,
    CardBody,
    CardHeader,
    Col,
    Container,
    Input,
    Row,
    Spinner,
} from "reactstrap";
import SimpleBar from "simplebar-react";
import "simplebar-react/dist/simplebar.min.css";
import { useChatbotClient } from "../../helpers/api_helper";
import useSpeechRecognition from "../../Components/Hooks/useSpeechRecognition";
import MicButton from "../../Components/Common/MicButton";
import botIcon from "../../assets/images/svg/crypto-icons/chat.svg";
import userIcon from "../../assets/images/users/user-dummy-img.jpg";

const Chatbot = () => {
    const chatbotClient = useMemo(() => useChatbotClient(), []);

    const [messages, setMessages] = useState([
        {
            role: "assistant",
            content: "Hi, I am your AI assistant. Ask me anything.",
            at: new Date().toISOString(),
        },
    ]);
    const [input, setInput] = useState("");
    const [isSending, setIsSending] = useState(false);
    const [error, setError] = useState(null);
    const [isMuted, setIsMuted] = useState(false); // 🔇 mute toggle for TTS audio

    // ── Previous-question linking ────────────────────────────────────────────
    // Holds the last user question + assistant answer for chat_history context
    const [lastExchange, setLastExchange] = useState({
        userMessage: "",
        assistantMessage: "",
    });
    // ────────────────────────────────────────────────────────────────────────

    // ── Voice (Part 1) ──────────────────────────────────────────────────────
    const { isListening, isSupported, startListening, stopListening } =
        useSpeechRecognition();
    // Keeps the latest transcript so onEnd closure never reads stale state
    const inputRef = useRef("");
    // Always points to the latest sendMessage — avoids stale closure in onEnd
    const sendMessageRef = useRef(null);
    // Tracks whether the current send was triggered by mic (for voice_enabled flag)
    const isVoiceTriggerRef = useRef(false);
    // Holds the currently playing Audio object so mute can stop it mid-playback
    const currentAudioRef = useRef(null);
    // ────────────────────────────────────────────────────────────────────────

    const scrollRef = useRef(null);

    document.title = "AI Chatbot | Whistlemind Demo Engine";

    useEffect(() => {
        const node = scrollRef.current;
        if (node) {
            node.scrollTop = node.scrollHeight;
        }
    }, [messages]);

    const extractReply = (raw) => {
        const payload = raw?.data ?? raw;
        const result = payload?.result;

        return (
            payload?.response ||
            result?.response ||
            result?.reply ||
            result?.answer ||
            payload?.reply ||
            payload?.answer ||
            payload?.message ||
            "I could not generate a response."
        );
    };

    // Accepts an optional override text + voice trigger flag
    // voiceTrigger=true → sends voice_enabled:true and plays back audio_base64
    const sendMessage = async (overrideText, voiceTrigger = false) => {
        const text = (overrideText ?? input).trim();
        if (!text || isSending) return;

        const userMessage = {
            role: "user",
            content: text,
            at: new Date().toISOString(),
        };

        setMessages((prev) => [...prev, userMessage]);
        setInput("");
        inputRef.current = "";
        setIsSending(true);
        setError(null);

        // ── Build request payload ─────────────────────────────────────────────
        const requestPayload = { question: text };

        // Include voice flag (Part 3)
        if (voiceTrigger) {
            requestPayload.voice_enabled = true;
        }

        // Include chat_history only when a prior exchange exists
        if (lastExchange.userMessage && lastExchange.assistantMessage) {
            requestPayload.chat_history = [
                { role: "user", message: lastExchange.userMessage },
                { role: "assistant", message: lastExchange.assistantMessage },
            ];
        }
        // ─────────────────────────────────────────────────────────────────────

        try {
            const payload = await chatbotClient.create("/chatbot-demo", requestPayload);
            const botText = extractReply(payload);
            const botMessage = {
                role: "assistant",
                content: botText,
                at: new Date().toISOString(),
            };

            setMessages((prev) => [...prev, botMessage]);

            // ── Update lastExchange for the next request ──────────────────────
            setLastExchange({
                userMessage: text,
                assistantMessage: botText,
            });
            // ─────────────────────────────────────────────────────────────────

            // ── Auto-play TTS audio if backend returned it (Part 3) ──────────
            const audioBase64 =
                payload?.audio_base64 ||
                payload?.data?.audio_base64 ||
                payload?.result?.audio_base64;

            if (audioBase64 && !isMuted) {
                try {
                    // Stop any previously playing audio before starting new one
                    if (currentAudioRef.current) {
                        currentAudioRef.current.pause();
                        currentAudioRef.current.currentTime = 0;
                    }
                    const audio = new Audio(`data:audio/mp3;base64,${audioBase64}`);
                    currentAudioRef.current = audio; // keep reference so mute can stop it
                    audio.onended = () => { currentAudioRef.current = null; }; // clean up when done
                    audio.play();
                } catch (audioErr) {
                    console.warn("[Chatbot] Audio playback failed:", audioErr);
                }
            }
            // ─────────────────────────────────────────────────────────────────

        } catch (err) {
            setError(err?.response?.data?.message || "Something went wrong. Please try again.");
        } finally {
            setIsSending(false);
        }
    };
    // Keep the ref in sync with the latest sendMessage on every render
    sendMessageRef.current = sendMessage;

    // ── Voice handlers ──────────────────────────────────────────────────────
    const handleMicClick = useCallback(() => {
        if (isListening) {
            stopListening();
            return;
        }
        // onTranscript: update input live as user speaks
        const onTranscript = (transcript) => {
            setInput(transcript);
            inputRef.current = transcript;
        };
        // onEnd: wait 2 s then auto-send via ref, passing voiceTrigger=true
        const onEnd = () => {
            const captured = inputRef.current.trim();
            if (captured) {
                setTimeout(() => {
                    sendMessageRef.current?.(captured, true); // true = voice trigger
                }, 2000);
            }
        };
        startListening(onTranscript, onEnd);
    }, [isListening, startListening, stopListening]);

    const handleSubmit = (e) => {
        e.preventDefault();
        sendMessage();
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    const handleClear = () => {
        setMessages([
            {
                role: "assistant",
                content: "Hi, I am your AI assistant. Ask me anything.",
                at: new Date().toISOString(),
            },
        ]);
        setError(null);
    };

    return (
        <div className="">
            <Container fluid>
                <Row className="justify-content-center">
                    <Col lg={12}>
                        <Card>
                            <CardHeader className="d-flex align-items-center justify-content-between">
                                <div className="d-flex align-items-center gap-2">
                                    <img src={botIcon} alt="Chatbot" width={28} height={28} />
                                    <h5 className="mb-0">Warehouse Bot</h5>
                                    <p className="text-muted mb-0">
                                        Ask questions and get instant answers from the assistant.
                                    </p>
                                </div>
                                <div className="d-flex gap-2">
                                    <Button
                                        color="light"
                                        size="sm"
                                        onClick={handleClear}
                                        disabled={isSending || messages.length <= 1}
                                    >
                                        Clear chat
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardBody className="d-flex flex-column" style={{ minHeight: "70vh" }}>
                                <div className="flex-grow-1 border rounded p-3 bg-light-subtle">
                                    <SimpleBar
                                        scrollableNodeProps={{ ref: scrollRef }}
                                        style={{ maxHeight: "58vh" }}
                                    >
                                        {messages.map((msg, idx) => (
                                            <div
                                                key={`${msg.role}-${idx}-${msg.at}`}
                                                className={`d-flex mb-3 ${msg.role === "user"
                                                    ? "justify-content-end"
                                                    : "justify-content-start"
                                                    }`}
                                            >
                                                <div
                                                    className={`p-3 rounded-3 shadow-sm ${msg.role === "user"
                                                        ? "bg-primary text-white"
                                                        : "bg-white border"
                                                        }`}
                                                    style={{ whiteSpace: "pre-wrap" }}
                                                >
                                                    <div className="d-flex align-items-center gap-2 small fw-semibold mb-1">
                                                        <img
                                                            src={msg.role === "user" ? userIcon : botIcon}
                                                            alt={msg.role === "user" ? "You" : "Assistant"}
                                                            width={18}
                                                            height={18}
                                                            className="rounded-circle"
                                                        />
                                                        <span>{msg.content}</span>
                                                    </div>
                                                    <div className="mb-0"></div>
                                                </div>
                                            </div>
                                        ))}
                                        {isSending && (
                                            <div className="d-flex mb-3 justify-content-start">
                                                <div className="p-3 rounded-3 border bg-white d-flex align-items-center gap-2">
                                                    <Spinner size="sm" color="primary" />
                                                    <span className="text-muted">Thinking...</span>
                                                </div>
                                            </div>
                                        )}
                                    </SimpleBar>
                                </div>

                                {error && (
                                    <Alert color="danger" className="mt-3 mb-0">
                                        {error}
                                    </Alert>
                                )}

                                <form onSubmit={handleSubmit} className="mt-3">
                                    <Row className="g-2 align-items-center">
                                        <Col xs={12} md={9} lg={10}>
                                            <div className="d-flex align-items-center gap-2">
                                                <Input
                                                    type="textarea"
                                                    rows="1"
                                                    value={input}
                                                    onChange={(e) => {
                                                        setInput(e.target.value);
                                                        inputRef.current = e.target.value;
                                                    }}
                                                    onKeyDown={handleKeyDown}
                                                    placeholder={
                                                        isListening
                                                            ? "Listening… speak now"
                                                            : "Type your question or use the mic…"
                                                    }
                                                    disabled={isSending || isListening}
                                                    className="chat-input bg-light border-0 flex-grow-1"
                                                />
                                                <MicButton
                                                    isListening={isListening}
                                                    isSupported={isSupported}
                                                    disabled={isSending}
                                                    onClick={handleMicClick}
                                                />
                                                {/* Mute / Unmute toggle — right of mic */}
                                                <button
                                                    type="button"
                                                    title={isMuted ? "Unmute voice response" : "Mute voice response"}
                                                    aria-label={isMuted ? "Unmute voice response" : "Mute voice response"}
                                                    onClick={() => setIsMuted((m) => {
                                                        const next = !m;
                                                        // If switching TO muted, stop any playing audio immediately
                                                        if (next && currentAudioRef.current) {
                                                            currentAudioRef.current.pause();
                                                            currentAudioRef.current.currentTime = 0;
                                                            currentAudioRef.current = null;
                                                        }
                                                        return next;
                                                    })}
                                                    style={{
                                                        display: "inline-flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        width: 40,
                                                        height: 40,
                                                        borderRadius: "50%",
                                                        border: "none",
                                                        background: isMuted ? "#dc3545" : "#0ab39c",
                                                        cursor: "pointer",
                                                        transition: "background 0.2s, transform 0.15s",
                                                        flexShrink: 0,
                                                    }}
                                                    onMouseEnter={(e) => e.currentTarget.style.transform = "scale(1.1)"}
                                                    onMouseLeave={(e) => e.currentTarget.style.transform = "scale(1)"}
                                                >
                                                    {isMuted ? (
                                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="#fff">
                                                            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                                                            <line x1="23" y1="9" x2="17" y2="15" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
                                                            <line x1="17" y1="9" x2="23" y2="15" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
                                                        </svg>
                                                    ) : (
                                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="#fff">
                                                            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                                                            <path d="M19.07 4.93a10 10 0 0 1 0 14.14" stroke="#fff" strokeWidth="2" strokeLinecap="round" fill="none" />
                                                            <path d="M15.54 8.46a5 5 0 0 1 0 7.07" stroke="#fff" strokeWidth="2" strokeLinecap="round" fill="none" />
                                                        </svg>
                                                    )}
                                                </button>
                                            </div>
                                        </Col>
                                        <Col xs={12} md={3} lg={2} className="text-md-end">
                                            <Button
                                                color="success"
                                                type="submit"
                                                className="w-100"
                                                disabled={isSending || isListening}
                                            >
                                                {isSending ? (
                                                    <>
                                                        <Spinner size="sm" className="me-2" /> Sending
                                                    </>
                                                ) : (
                                                    "Send"
                                                )}
                                            </Button>
                                        </Col>
                                    </Row>
                                </form>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default Chatbot;
